import { IconSearch } from '@developerz/ui';
import { batch, createSignal, createUniqueId, type JSX, Show, untrack } from 'solid-js';
import { t } from '../i18n';
import {
  GRANT_SUBJECT_KIND_OPTIONS,
  type GrantSubject,
  type GrantSubjectKind,
  useGrantSubjectSearch,
} from '../lib/adminGrants';
import { DataTable } from './DataTable';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Select } from './Select';
import { Skeleton } from './Skeleton';
import { Button, buttonClass } from './ui/Button';

// WHOM a grant addresses, picked from the registry rather than typed.
//
// This is the correctness half of the screen, not a convenience. The listing
// route does NOT resolve the subject — an id nobody holds answers `{data: []}`,
// which is indistinguishable from "this org has no grants" — so a pasted uuid
// with one character wrong reads as an empty history and an operator issues a
// duplicate. Taking the id from `/v1/admin/{accounts,users}`'s own answer is the
// one shape that cannot be mistyped.
//
// CHANGING THE KIND DROPS THE SELECTION. The two axes lift different gates
// (`GRANT_SUBJECT_KINDS`) and a `users.id` is not an `accounts.id`, so a subject
// carried across the toggle would address a row nobody holds.

// The results are the registry the kind names, so the table and its one column
// say which. Literal `t()` calls per entry, so `i18n.coverage.test.ts` sees them.
const REGISTRY_LABEL: Readonly<Record<GrantSubjectKind, () => string>> = {
  account: () => t('admin.orgs_table_label'),
  user: () => t('admin.users_table_label'),
};
const COLUMN_HEADER: Readonly<Record<GrantSubjectKind, () => string>> = {
  account: () => t('admin.col_org'),
  user: () => t('admin.col_user'),
};

export function AdminGrantSubjectPicker(props: {
  readonly token: string | null;
  readonly subject: GrantSubject | null;
  readonly onPick: (subject: GrantSubject) => void;
  readonly onClear: () => void;
}): JSX.Element {
  // Seeded from the subject handed in (the user registry's Grant link passes a
  // person): opening on `account` above a picked person searched the wrong
  // registry. Read once, like the panel's own `initialSubject`; `changeKind` owns it after.
  const [kind, setKind] = createSignal<GrantSubjectKind>(
    untrack(() => props.subject?.kind) ?? 'account',
  );
  const kindId = createUniqueId();
  const [draft, setDraft] = createSignal('');
  const [q, setQ] = createSignal('');
  // #5914: track picked state locally so the EmptyState hint hides once the
  // visitor has picked. props.subject is reactive in production; tests hand in
  // a constant. Tracking here makes both behave the same.
  const [hasPicked, setHasPicked] = createSignal(false);
  const results = useGrantSubjectSearch(kind, q, props.token);

  // The new query and nothing else: a re-search for the SAME text stays a no-op
  // rather than re-firing a read whose answer is already on screen.
  const search = (): void => {
    const next = draft().trim();
    if (next !== q()) setQ(next);
  };

  const changeKind = (next: GrantSubjectKind): void =>
    batch(() => {
      setKind(next);
      setDraft('');
      setQ('');
      setHasPicked(false);
      props.onClear();
    });

  return (
    <Panel title={t('admin.grants_subject_title')}>
      {/* A semantic <search>, NOT a <form>: a real <form> as a static sibling of
          an async list <For/> trips a happy-dom template-walk crash (see
          <AdminOrgsPanel/>). Search fires on the button or Enter. */}
      <search class="filter-row">
        <div class="admin-filter__select">
          <label id={`${kindId}-label`} for={kindId} class="sr-only">
            {t('admin.grants_subject_kind_label')}
          </label>
          <Select
            a11y={{ id: kindId }}
            label={t('admin.grants_subject_kind_label')}
            value={kind()}
            options={GRANT_SUBJECT_KIND_OPTIONS.map((k) => ({
              value: k,
              label: t(`admin.grants_subject_kind_${k}`),
            }))}
            onChange={changeKind}
          />
        </div>
        <div class="search-input">
          <IconSearch size="1rem" />
          <input
            id="admin-grants-subject-q"
            type="search"
            class="input"
            placeholder={t('admin.grants_subject_search')}
            aria-label={t('admin.grants_subject_search')}
            value={draft()}
            onInput={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') search();
            }}
          />
        </div>
        <Button variant="outline" onClick={search}>
          {t('admin.search')}
        </Button>
      </search>

      <Show when={props.subject}>
        {(picked) => (
          <p class="admin-grants__picked">
            <span class="data-table__mono">{picked().label}</span>
            <span class="admin-grants__meta">{picked().id}</span>
            <Button variant="ghost" size="sm" onClick={props.onClear}>
              {t('admin.grants_subject_clear')}
            </Button>
          </p>
        )}
      </Show>

      <QueryBoundary
        query={results}
        isEmpty={(rows) => rows.length === 0}
        loading={<Skeleton height="6rem" />}
        empty={
          // #5914: the "search for an organization or a person" hint reads as
          // "nothing is picked" once the picked-subject pill is showing above
          // the search. The picker tracks hasPicked locally so this stays accurate
          // whether props.subject is reactive (production) or a constant (tests).
          <Show when={!hasPicked()}>
            <EmptyState
              message={q() === '' ? t('admin.grants_subject_hint') : t('admin.grants_subject_none')}
            />
          </Show>
        }
      >
        {(rows) => (
          <DataTable
            label={REGISTRY_LABEL[kind()]()}
            columns={[
              {
                key: 'subject',
                rowHeader: true,
                header: COLUMN_HEADER[kind()](),
                cell: (option) => (
                  <>
                    <button
                      type="button"
                      class={`${buttonClass({ variant: 'link' })} data-table__mono`}
                      onClick={() => {
                        setHasPicked(true);
                        props.onPick({ kind: kind(), id: option.id, label: option.label });
                      }}
                    >
                      {option.label}
                    </button>
                    <Show when={option.hint}>
                      <span class="admin-grants__meta">{option.hint}</span>
                    </Show>
                  </>
                ),
              },
            ]}
            rows={rows}
            rowKey={(option) => option.id}
            rowCurrent={(option) => props.subject?.id === option.id}
          />
        )}
      </QueryBoundary>
    </Panel>
  );
}
